import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  ArrowLeft, Receipt, ExternalLink, Info, Calendar, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { PAYG_HEADLINE_PHRASE } from '../../config/pricing';

/**
 * PAYG Usage Statement (Feb 2026 iter88).
 *
 * Reconciles the internal `billing_ledger` (source of truth) against
 * the consolidated monthly Stripe invoice for a manager on the
 * Pay-As-You-Go plan.
 *
 * Design contract:
 *  - Managers see ONLY their own branches (backend scopes by managerId).
 *  - The month selector shows the current month + every historical month
 *    with any recorded shift. Empty months render an explicit empty state.
 *  - Each row surfaces: shift reference, branch, shift date, completed at,
 *    per-shift charge, billing status, and the linked Stripe invoice
 *    (when reconciled).
 *  - The internal ledger total is displayed alongside the invoice total —
 *    any drift is shown as an amber "reconciling" pill instead of hidden.
 */
export default function UsageStatementPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [data, setData] = useState(null);

  const fetchStatement = useCallback(async (month) => {
    setLoading(true);
    try {
      const url = month ? `/billing/usage-statement?month=${month}` : '/billing/usage-statement';
      const res = await api.get(url);
      setData(res.data);
      setSelectedMonth((prev) => prev || res.data.selectedMonth);
    } catch {
      toast.error('Could not load your usage statement. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchStatement();
  }, [fetchStatement]);

  const monthOptions = useMemo(() => data?.months || [], [data]);

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
    fetchStatement(value);
  };

  if (loading && !data) {
    return (
      <div className="max-w-4xl mx-auto p-4 lg:p-8 space-y-6" data-testid="usage-statement-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const rows = data?.rows || [];
  const totals = data?.totals || { count: 0, amountPence: 0, amountDisplay: '£0' };
  const invoice = data?.invoice || {};
  const reconciled = invoice.reconciled;
  const meterEventName = data?.meterEventName;
  const invoicePence = invoice?.invoiceAmountPence;

  return (
    <div
      className="max-w-4xl mx-auto p-4 lg:p-8 space-y-6"
      data-testid="usage-statement-page"
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/manager/billing')}
          data-testid="usage-back-btn"
          aria-label="Back to Billing"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">
            Usage Statement
          </h1>
          <p className="text-sm text-slate-600 mt-1" data-testid="usage-payg-phrase">
            {data?.paygPhrase || PAYG_HEADLINE_PHRASE}
          </p>
        </div>
      </div>

      {/* Month selector */}
      <Card className="border-slate-200/70">
        <CardContent className="p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-slate-500" />
              <div>
                <div className="text-xs uppercase font-semibold text-slate-500 tracking-wide">
                  Billing month
                </div>
                <div className="text-lg font-semibold text-slate-900" data-testid="usage-selected-month-label">
                  {data?.selectedMonthLabel}
                </div>
              </div>
            </div>
            <Select value={selectedMonth || data?.selectedMonth} onValueChange={handleMonthChange}>
              <SelectTrigger
                className="w-full sm:w-56"
                data-testid="usage-month-select"
                aria-label="Choose billing month"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((m) => (
                  <SelectItem key={m.key} value={m.key} data-testid={`usage-month-option-${m.key}`}>
                    {m.label} {m.isCurrent ? '(This month)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Summary card */}
      <Card className="border-slate-200/70" data-testid="usage-summary-card">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-xs uppercase font-semibold text-slate-500 tracking-wide">
                Shifts covered
              </div>
              <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="usage-total-count">
                {totals.count}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase font-semibold text-slate-500 tracking-wide">
                Ledger total
              </div>
              <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="usage-total-amount">
                {totals.amountDisplay}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase font-semibold text-slate-500 tracking-wide">
                Stripe invoice
              </div>
              {invoice?.stripeInvoiceId ? (
                <a
                  href={invoice.stripeInvoiceUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-lg font-semibold text-red-600 hover:text-red-700 mt-1"
                  data-testid="usage-invoice-link"
                >
                  View invoice <ExternalLink className="w-4 h-4" />
                </a>
              ) : (
                <div className="text-sm text-slate-500 mt-2" data-testid="usage-invoice-pending">
                  Invoice issued at end of month
                </div>
              )}
              {invoice?.stripeInvoiceId && (
                <div className="mt-1">
                  {reconciled ? (
                    <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50" data-testid="usage-reconciled-badge">
                      Reconciled ✓
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50" data-testid="usage-reconciling-badge">
                      Reconciling — Stripe {typeof invoicePence === 'number' ? `£${(invoicePence / 100).toFixed(2)}` : ''}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3">
            <Info className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
            <p className="text-xs text-slate-600 leading-relaxed">
              Each completed shift is recorded to your internal ledger and reported to Stripe via
              usage events{meterEventName ? ` (${meterEventName})` : ''}. You are billed once per
              calendar month — no per-shift charges will appear on your statement.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Line items */}
      {rows.length === 0 ? (
        <Card className="border-dashed border-slate-300 bg-slate-50/50" data-testid="usage-empty-state">
          <CardContent className="p-10 text-center">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-slate-900">
              No billable shifts yet this month
            </h3>
            <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
              You&apos;ll see every successfully completed shift here as workers finish them. Nothing
              is charged until the end of the calendar month.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200/70" data-testid="usage-rows-card">
          <CardContent className="p-0">
            <div className="hidden md:grid md:grid-cols-6 gap-2 px-5 py-3 border-b border-slate-200 bg-slate-50/50 text-xs uppercase font-semibold text-slate-500 tracking-wide">
              <div>Reference</div>
              <div>Branch</div>
              <div>Shift date</div>
              <div>Completed</div>
              <div>Charge</div>
              <div>Status</div>
            </div>
            <ul className="divide-y divide-slate-100">
              {rows.map((row) => (
                <li
                  key={row.ledgerId || row.shiftId}
                  className="grid grid-cols-2 md:grid-cols-6 gap-2 px-5 py-4 items-center"
                  data-testid={`usage-row-${row.shiftId}`}
                >
                  <div className="font-mono text-sm font-semibold text-slate-900">
                    {row.shiftReference}
                  </div>
                  <div className="text-sm text-slate-700 truncate">{row.branchName}</div>
                  <div className="text-sm text-slate-600">{row.shiftDate}</div>
                  <div className="text-sm text-slate-600">
                    {row.completedAt ? new Date(row.completedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </div>
                  <div className="text-sm font-semibold text-slate-900">
                    {row.amountDisplay}
                  </div>
                  <div className="text-xs">
                    <Badge
                      variant="outline"
                      className={
                        row.meterEventStatus === 'emitted'
                          ? 'text-emerald-700 border-emerald-300 bg-emerald-50'
                          : row.meterEventStatus === 'failed'
                            ? 'text-amber-700 border-amber-300 bg-amber-50'
                            : 'text-slate-600 border-slate-300 bg-slate-50'
                      }
                      data-testid={`usage-row-status-${row.shiftId}`}
                    >
                      {row.billingStatus}
                    </Badge>
                    {row.stripeInvoiceId && (
                      <a
                        href={row.stripeInvoiceUrl || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
                        data-testid={`usage-row-invoice-${row.shiftId}`}
                      >
                        <Receipt className="w-3 h-3" /> Invoice
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
